<template>
    <div>
        <div
            v-if="id"
            class="flex flex-col md:flex-row justify-between items-center"
        >
            <h1 class="text-3xl">
                Detalles de tienda
                <strong class="text-theme-primary"> #{{ id }} </strong>
            </h1>
            <custom-switch
                v-if="$ability.hasPermission('store:UpdateOwn')"
                v-model="enabledUpdate"
            >
                <span class="text-theme-inverse">🖊</span>
            </custom-switch>
        </div>
        <div v-else>
            <h1 class="text-3xl">Registrar nueva tienda</h1>
        </div>

        <div class="mt-3">
            <span v-if="isOnboarding">
                Comienza a crear tu primera tienda. Ingresa el nombre que desees
                , los datos de contacto y los datos de ubicación.
            </span>
            <fragment v-else>
                <span v-if="id">
                    Manten actualizada la información de tu tienda.
                </span>
                <span v-else>
                    En esta sección puedes registrar tantas tiendas como
                    necesites.
                </span>
            </fragment>
        </div>

        <div class="flex flex-wrap">
            <div v-if="id" class="w-full mt-3 p-0 flex justify-around">
                <span class="text-left">
                    📆💾 {{ itemComponent.createdAt | date }}
                </span>
                <span class="text-left">
                    📆✏ {{ itemComponent.updatedAt | date }}
                </span>
            </div>
            <div class="w-full md:w-1/2 px-2">
                <custom-input
                    v-model="itemComponent.name"
                    label="Nombre de tienda"
                    :disabled="!enabledUpdate"
                    :error="
                        !isValidName & introValidators.name
                            ? 'Nombre de tienda invalido'
                            : ''
                    "
                />
            </div>

            <div class="w-full md:w-1/2 px-2">
                <custom-input
                    v-model="itemComponent.phone"
                    class="w-full"
                    label="Celular de contacto"
                    type="number"
                    :disabled="!enabledUpdate"
                    :error="
                        !isValidPhone & introValidators.phone
                            ? 'Celular invalido'
                            : ''
                    "
                    @blur="introValidators.phone = true"
                />
            </div>

            <div class="w-full md:w-1/2 mt-6 px-2">
                <city-code-selector
                    v-model="itemComponent.cityCode"
                    :enabled="enabledUpdate"
                />
            </div>

            <div class="w-full md:w-1/2 px-2">
                <custom-input
                    v-model="itemComponent.address"
                    class="w-full"
                    label="Dirección. Ej. Cll 42 # 12 - 8"
                    placeholder="Cll 42 # 12 - 8"
                    :disabled="!enabledUpdate"
                    :error="
                        !isValidAddress & introValidators.address
                            ? 'Dirección invalida'
                            : ''
                    "
                    @blur="onBlurAddress"
                />
            </div>

            <div class="w-full md:w-1/2 px-2">
                <custom-input
                    v-model="itemComponent.addressComplement"
                    class="w-full"
                    label="Complemento de Dirección. Ej Local 12"
                    placeholder="Local 12"
                    :disabled="!enabledUpdate"
                />
            </div>

            <div class="w-full md:w-1/2 px-2">
                <custom-input
                    v-model="itemComponent.neighborhood"
                    class="w-full"
                    label="Barrio / Localidad"
                    :disabled="!enabledUpdate"
                />
            </div>

            <fragment v-if="id">
                <div class="w-full md:w-1/2 px-2">
                    <custom-input
                        v-model="itemComponent.identificationNumber"
                        class="w-full"
                        label="Identificación (Optional)"
                        :disabled="!enabledUpdate"
                    />
                </div>

                <div class="w-full md:w-1/2 mt-6 px-2">
                    <coordinates-selector
                        v-model="itemComponent.coordinates"
                        :enabled="enabledUpdate"
                    />
                </div>
            </fragment>

            <div v-if="enabledUpdate" class="w-full mt-4 flex justify-center">
                <app-button
                    v-if="!id"
                    size="large"
                    @click="createItemComponent"
                >
                    Crear Tienda
                </app-button>
                <app-button v-else size="large" @click="updateItemComponent">
                    Actualizar Tienda
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
import planLimitError from "@/mixins/planLimitError";
import { mapActions, mapGetters } from "vuex";

import { addressRegex, phoneRegex, titleRegex } from "@/common/lib/regex";

import CityCodeSelector from "@/components/utils/CityCodeSelector.vue";
import CoordinatesSelector from "@/components/utils/CoordinatesSelector.vue";
import CustomInput from "@/components/utils/CustomInput.vue";

export default {
    name: "StoreReduced",
    components: {
        CoordinatesSelector,
        CityCodeSelector,
        CustomInput
    },
    mixins: [planLimitError],
    data: () => ({
        id: "",
        isLoading: false,
        enabledUpdate: false,
        loadedAllData: false,
        openActiveModal: false,
        isOnboarding: false,
        itemComponent: {
            id: "",
            identificationNumber: "",
            identificationType: "",
            name: "",
            phone: "",
            address: "",
            cityCode: "11001",
            neighborhood: "",
            addressComplement: "",
            coordinates: { latitude: 4.63082, longitude: -74.15035 }
        },
        introValidators: {
            phone: false,
            address: false,
            name: false
        }
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        isValidAddress() {
            return addressRegex.test(this.itemComponent.address);
        },
        isValidPhone() {
            return phoneRegex.test(this.itemComponent.phone);
        },
        isValidName() {
            return titleRegex.test(this.itemComponent.name);
        }
    },
    watch: {},
    created() {
        this.$watch(
            () => this.$route,
            (newVal, oldVal) => {
                if (newVal !== oldVal) {
                    location.reload();
                }
            },
            {
                deep: true
            }
        );
        this.isOnboarding = this.$route.path.includes("onboarding");
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if ("id" in this.$route.params) {
            if (!(await verifyPagePermission("store:GetOwn", "/app"))) return;
            const id = this.$route.params.id;
            this.id = id;
            await this.getBaseItem(id);
        } else {
            if (!(await verifyPagePermission("store:CreateOwn", "/app")))
                return;
            this.enabledUpdate = true;
            this.loadedAllData = true;
        }
    },
    methods: {
        ...mapActions("stores", [
            "getStoreOwnById",
            "updateStoreOwn",
            "createStoreOwn"
        ]),
        async getBaseItem(id) {
            this.isLoading = true;
            this.$loading.show();
            try {
                const data = await this.getStoreOwnById({ id });
                this.itemComponent = data;
                this.loadedAllData = true;
                this.isLoading = false;
                this.$loading.hide();
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                this.isLoading = false;
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
                if (error.statusCode === 404) {
                    this.$router.push("/app/stores");
                }
            }
        },
        async updateItemComponent() {
            if (!this.validateData()) {
                return;
            }
            this.isLoading = true;
            this.$loading.show();
            try {
                await this.updateStoreOwn(this.getDataSend());
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.$loading.hide();
                this.isLoading = false;
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async createItemComponent() {
            if (!this.validateData()) {
                return;
            }
            this.isLoading = true;
            this.$loading.show();
            try {
                const data = await this.createStoreOwn(this.getDataSend());
                this.$sound.play();
                this.$loading.hide();
                if (this.$route.path.includes("onboarding")) {
                    await this.$swal.fire({
                        background: this.backgroundColor,
                        title: "Creación exitosa",
                        icon: "success",
                        timer: 3000
                    });
                    return this.$router.push("/onboarding/inventories");
                }
                const dialogResult = await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Creación exitosa",
                    text: `Id: ${data.merchantId}`,
                    icon: "success",
                    showDenyButton: true,
                    showCloseButton: true,
                    showCancelButton: true,
                    confirmButtonColor: "#02672D",
                    cancelButtonColor: "#253C99",
                    denyButtonColor: "#d33ddd",
                    confirmButtonText: "Ver detalle",
                    denyButtonText: "Ir a inicio",
                    cancelButtonText: "Nueva tienda"
                });
                if (dialogResult.isConfirmed) {
                    this.id = data.merchantId;
                    this.itemComponent = {
                        ...this.itemComponent,
                        id: data.merchantId,
                        ...data
                    };
                    this.$router.push(`/app/stores/reduced/${this.id}`);
                }
                if (dialogResult.isDenied) {
                    this.$router.push("/app/");
                }
                if (dialogResult.isDismissed) {
                    this.introValidators = {
                        phone: false,
                        address: false,
                        name: false
                    };
                    this.itemComponent = {
                        id: "",
                        identificationNumber: "",
                        identificationType: "",
                        name: "",
                        phone: "",
                        address: "",
                        cityCode: "11001",
                        neighborhood: "",
                        addressComplement: ""
                    };
                }
                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                if (this.openPlanUpgradeForLimit(error)) return;
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
                if (error.statusCode === 404) {
                    this.$router.push("/app/stores");
                }
            }
        },
        validateData() {
            this.introValidators = {
                phone: true,
                address: true,
                name: true
            };

            const toComplete = [];

            if (!this.isValidName) {
                toComplete.push("Nombre");
            }

            if (!this.isValidPhone) {
                toComplete.push("Celular");
            }

            if (!this.isValidAddress) {
                toComplete.push("Dirección");
            }

            if (toComplete.length) {
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Tienda invalida",
                    text: `Revisa por favor revisa los siguientes campos: ${toComplete.join(
                        ", "
                    )}`,
                    icon: "info"
                });
                return false;
            }
            return true;
        },
        getDataSend() {
            return {
                ...this.itemComponent,
                id: this.id
            };
        },
        onBlurAddress() {
            this.introValidators.address = true;
            this.itemComponent.address = this.itemComponent.address.trim();
        }
    }
};
</script>
